import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

/**
 * Terms of Service — APPROVED FINAL (May 26, 2026).
 *
 * Covers: account terms, marketplace livestock-sale disclaimers,
 * Storefront marketplace-facilitator carve-out, Armstrong Transport
 * independent-contractor terms, escrow & dispute resolution SLA,
 * arbitration / class waiver, acceptable use, IP, DMCA, termination.
 *
 * Linked from the global footer + post-signup confirmation. Section
 * numbering follows the published copy — do not renumber sections
 * without legal review.
 */
export default function TermsOfService() {
  return (
    <LegalLayout
      slug="terms"
      eyebrow="Legal"
      title="Terms of Service"
      lastUpdated="June 21, 2026"
    >
      <div
        data-testid="legal-dba-disclosure"
        className="text-[12px] text-[#6B6B6B] border-l-2 border-gold/40 pl-3 my-4 not-prose"
      >
        Armstrong K9 Registry is a trade name of <strong>Armstrong K9 Registry
        LLC</strong>, a California limited liability company (registered agent
        address: 4054 Highway 273, Anderson, CA 96007). References to
        &ldquo;Armstrong K9 Registry&rdquo; in this document mean Armstrong
        K9 Registry LLC and its successors and assigns.
      </div>
      <p>
        By accessing or using Armstrong K9 Registry (the
        &ldquo;Registry&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;) you agree
        to these Terms of Service. If you do not agree, do not use the
        Registry.
      </p>

      <div
        style={{
          margin: "24px 0",
          padding: "16px 18px",
          border: "2px solid #C0392B",
          background: "#FDECEC",
          borderRadius: 6,
          fontSize: "0.95rem",
          lineHeight: 1.55,
        }}
        data-testid="terms-venue-banner"
      >
        <p style={{ margin: 0, fontWeight: 700, color: "#7A1F12" }}>
          AT-A-GLANCE &mdash; VENUE ROLE AND AT-YOUR-OWN-RISK
        </p>
        <p style={{ margin: "8px 0 0", color: "#3B0E07" }}>
          <strong>Armstrong K9 Registry is a venue.</strong> We introduce
          independent breeders, buyers, sellers, service providers,
          transporters, and event hosts to one another. We are <strong>not</strong>{" "}
          a party to any transaction, sale, breeding, service, booking,
          transport, contract, or event arranged through the platform. We do
          not verify every claim made by every user, we are not the
          breeder/buyer/seller/employer/agent of any user, and we are not legal,
          veterinary, or financial counsel.{" "}
          <strong>You use the Registry at your own risk</strong> and accept
          that Armstrong K9 Registry is not liable for the acts, omissions, fitness,
          lineage claims, health, temperament, or outcomes of any third party
          introduced through the platform. Every paid action (publish,
          deposit, contract sign, booking, escrow, transport, storefront
          purchase, event RSVP) requires an on-record click-wrap
          acknowledgement of this posture in addition to these Terms.
        </p>
      </div>

      <h2>1. Eligibility</h2>
      <ul>
        <li>You must be at least 18 years old.</li>
        <li>
          You must not be located in, or a national of, a country subject to US
          embargoes (Cuba, Iran, North Korea, Syria, the Crimea region,
          Donetsk, Luhansk).
        </li>
        <li>You must not appear on any US Treasury OFAC sanctions list.</li>
      </ul>

      <h2>2. Your account</h2>
      <ul>
        <li>You are responsible for the security of your account credentials.</li>
        <li>You may not impersonate another person, kennel, or registry.</li>
        <li>
          One account per person. Kennel staff sub-accounts are available on
          the Kennel Elite plan.
        </li>
        <li>
          We may suspend or terminate accounts for fraud, abuse, or repeated
          breaches of these terms.
        </li>
        <li>
          <strong>Admin support access.</strong> To resolve technical issues
          you report, an authorized Armstrong K9 Registry admin may either
          (a) view a read-only snapshot of your account or (b) briefly sign in
          as you for up to 60 minutes. Every &ldquo;sign in as&rdquo; session is
          audit-logged and you will receive an email plus in-app inbox
          notification identifying the admin and the time of access. Full
          details are in Section 5 of our{" "}
          <Link to="/privacy">Privacy Policy</Link>.
        </li>
      </ul>

      <h2>3. Marketplace &mdash; important disclaimers</h2>
      <p>
        The Registry hosts a marketplace where breeders list dogs for sale and
        buyers may purchase them. <strong>We are a venue, not the seller.</strong>{" "}
        Each dog is sold by the listing breeder, not by us.
      </p>
      <ul>
        <li>
          <strong>Livestock laws vary by state.</strong> Buyers and sellers are
          responsible for complying with their local laws on dog sales,
          including waiting periods, mandatory health certifications,
          micro-chipping, vaccination disclosure, and resale restrictions.
        </li>
        <li>
          <strong>Restricted breeds.</strong> Certain breeds are banned or
          regulated in jurisdictions including the United Kingdom, Germany,
          Australia, and several US municipalities. The Registry surfaces a
          warning on listings for these breeds; buyers must verify legality in
          their location before purchasing.
        </li>
        <li>
          <strong>USDA APHIS.</strong> Buyers should verify USDA license status
          for any breeder selling more than four litters per year. The
          Registry does not certify USDA compliance.
        </li>
        <li>
          <strong>No dog fighting, attack-trained-for-humans, or
          breeding-to-kill</strong> language is permitted on listings or
          kennel bios. Listings violating this rule are removed and the posting
          account is suspended.
        </li>
      </ul>

      <h2>4. Escrow &amp; Dispute Resolution</h2>
      <p>
        Marketplace dog purchases route through Stripe Connect with funds held
        by the Registry until pickup is confirmed. The &ldquo;escrow&rdquo;
        term is used for clarity; the Registry holds funds as a non-fiduciary
        intermediary, not as a trustee.
      </p>

      <h3>4.1 Auto-release</h3>
      <p>
        If neither buyer nor seller raises a dispute, funds are released to the
        seller (less the platform fee) <strong>14 calendar days</strong> after
        payment.
      </p>

      <h3>4.2 Dispute resolution (mechanical, no admin discretion)</h3>
      <p>
        Disputes are governed by the venue-posture model published in the{" "}
        <Link to="/refund-policy">Refund Policy</Link>. Armstrong K9
        Registry <strong>does not adjudicate the merits of any dispute</strong>.
        The platform administers a published timeline; the parties decide
        (via mutual consent) or the clock decides (via the published
        default outcome).
      </p>
      <ul>
        <li>Disputes can be raised within 14 calendar days of payment by either party.</li>
        <li>
          Outcomes execute only by (a) mutual written agreement of buyer
          and seller via the in-platform consent flow, or (b) the
          published 30-day mechanical default after dispute filing
          (full refund to buyer for marketplace dog purchases; tiered
          default for transport per §6.3).
        </li>
        <li>
          Individual sellers may publish refund extensions (longer
          windows) on a per-kennel basis under Refund Policy §8b.
          Sellers cannot publish &ldquo;no refunds,&rdquo;
          &ldquo;final sale,&rdquo; or any clause that shortens the
          30-day floor or eliminates the buyer&rsquo;s right to file a
          dispute. Such clauses are void on this platform.
        </li>
        <li>
          The Registry will not weigh evidence, render decisions, or
          mediate disputes between parties. Stripe chargebacks remain
          available as a parallel recourse and are assessed against the
          seller&rsquo;s Stripe Connect account, not the Registry.
        </li>
      </ul>

      <h3>4.3 Buyer protections</h3>
      <ul>
        <li>Non-delivery (dog not transferred within agreed window).</li>
        <li>Material misrepresentation (breed, health, registration status).</li>
        <li>
          Health-guarantee violations as set out in the listing&rsquo;s
          structured guarantee block, when one is published.
        </li>
      </ul>

      <h3>4.4 Seller protections</h3>
      <ul>
        <li>Buyer no-show or refusal to accept delivery after agreed window.</li>
        <li>
          Buyer claims unsupported by evidence after the dog has been delivered
          in good condition.
        </li>
      </ul>

      <h2>5. Pricing &amp; payments</h2>
      <ul>
        <li>
          All prices are shown in USD. Currency conversion is your card
          issuer&rsquo;s responsibility.
        </li>
        <li>
          Subscriptions auto-renew at the displayed cycle (monthly or annual)
          until cancelled. You can cancel at any time from{" "}
          <Link to="/billing">Billing</Link>. Subscriptions purchased inside
          the iOS app are billed by Apple and are managed through your
          Apple&nbsp;ID instead &mdash; see Section 5h.
        </li>
        <li>
          Refund eligibility is set out in our{" "}
          <Link to="/refund">Refund Policy</Link>.
        </li>
      </ul>

      <h2>5a. Seller is the merchant of record &middot; Tax responsibility</h2>
      <p>
        For every dog, litter, breeding service, or other item listed on the
        marketplace by a seller (a &ldquo;Listing&rdquo;):
      </p>
      <ul>
        <li>
          The <strong>seller &mdash; not the Registry &mdash; is the merchant
          of record</strong> for the underlying sale. The Registry operates as
          a payment-routing venue using Stripe Connect Express. Funds flow
          from the buyer through the platform escrow to the seller&rsquo;s
          Stripe-managed account; the platform never holds beneficial
          ownership of the goods or revenue.
        </li>
        <li>
          <strong>Sales tax, use tax, and any other transaction-level taxes</strong>{" "}
          are the sole responsibility of the seller in their nexus state and
          any other jurisdiction where they have a tax obligation. The
          Registry does not calculate, collect, or remit such taxes on the
          seller&rsquo;s behalf.
        </li>
        <li>
          <strong>1099-K tax forms (US)</strong> are issued by Stripe directly
          to the seller&rsquo;s Stripe Connect account when annual payout
          volume crosses the IRS threshold. The Registry is not a party to
          that filing.
        </li>
        <li>
          <strong>Income tax, self-employment tax, business-license fees,
          USDA APHIS license fees</strong>, and any equivalent obligations
          outside the United States are the seller&rsquo;s responsibility.
        </li>
        <li>
          By creating a Listing, the seller affirmatively attests they are the
          merchant of record and assume sole responsibility for the tax
          obligations described in this section. This attestation is recorded
          in the Registry&rsquo;s audit log at listing time.
        </li>
        <li>
          <strong>Marketplace facilitator laws.</strong> The Registry operates
          below the volume thresholds at which most US states impose
          marketplace-facilitator collection duties on a platform. To the
          extent a particular jurisdiction unambiguously and mandatorily
          imposes such duties on the Registry notwithstanding this Agreement,
          the Registry will comply with that local requirement and only that
          requirement; in all other cases the seller remains the sole
          tax-responsible party.
        </li>
        <li>
          The seller indemnifies the Registry, its officers, employees, and
          contractors against any tax liability, penalty, interest, or
          assessment arising from the seller&rsquo;s failure to meet the
          obligations described in this section. See Section 12 for the
          general indemnification clause.
        </li>
      </ul>

      <h2>5b. The Dog Shop &mdash; marketplace facilitator role</h2>
      <p>
        The Registry offers subscription-gated breeder storefronts at{" "}
        <code>/store/&#123;slug&#125;</code> (collectively, &ldquo;The Dog Shop&rdquo;{" "}
        &mdash; formerly called &ldquo;Kennel Storefronts&rdquo;) where verified
        breeders (&ldquo;Storefront Sellers&rdquo;) sell dog food, treats,
        supplements, merch, and related goods directly to consumers. Storefront
        Sellers are independent third parties, not employees, agents, or
        partners of the Registry.
      </p>
      <ul>
        <li>
          <strong>Marketplace-facilitator role.</strong> The Registry provides
          the technology (page, catalog, checkout, payout routing) but is not
          the merchant, manufacturer, distributor, or shipper of any Storefront
          product. The Storefront Seller is the merchant of record for every
          sale.
        </li>
        <li>
          <strong>No warranty on Storefront goods.</strong> The Registry makes
          no representations or warranties &mdash; express or implied, including
          warranties of merchantability, fitness for a particular purpose,
          ingredient accuracy, safety, freshness, or compliance with FDA, USDA,
          state-feed, or any other regulation &mdash; regarding any product
          listed on a Storefront. All such warranties (if any) flow solely
          between the Storefront Seller and the buyer.
        </li>
        <li>
          <strong>Payments.</strong> Storefront checkouts use Stripe Connect
          destination charges: buyers are charged on the platform&rsquo;s
          Stripe account, the platform retains a 5% application fee, and the
          remaining funds are routed to the Storefront Seller&rsquo;s connected
          Stripe account in the same transaction. The Registry does not custody
          Storefront funds and does not control payout timing &mdash; Stripe
          does.
        </li>
        <li>
          <strong>Delivery, returns, and customer support.</strong> Each
          Storefront Seller sets and enforces their own local-delivery zone,
          shipping policies, and customer-support channels. Refunds, returns,
          replacements, lost-package claims, allergic reactions, ingredient
          disputes, and any other product-quality issue are the sole
          responsibility of the Storefront Seller.
        </li>
        <li>
          <strong>Disputes and chargebacks.</strong> Storefront disputes and
          chargebacks are handled directly between the buyer, the Storefront
          Seller, and Stripe. The Registry is not a party to such disputes and
          will not mediate, indemnify, or otherwise be financially responsible
          for their outcome. The Registry&rsquo;s escrow protections described
          in Section 4 apply only to peer-to-peer dog-sale transactions, not
          to Storefront purchases.
        </li>
        <li>
          <strong>Sales tax.</strong> The Storefront Seller is the seller
          of record for sales-tax purposes. The Registry uses Stripe Tax
          to calculate and collect sales tax on the Seller&rsquo;s
          behalf at checkout and transfers it to the Seller&rsquo;s
          Stripe Connect account along with the order total. The Seller
          remains solely responsible for remitting that tax to the
          appropriate authorities, for registering and maintaining nexus
          where required, for filing returns, and for handling any
          audit, refund, or mis-calculation dispute. The Registry is not
          a tax advisor or remittance agent.
        </li>
        <li>
          <strong>Prohibited Storefront products and services.</strong>{" "}
          Storefronts are limited to <em>physical goods</em>. Storefronts
          may <strong>not</strong> list or sell any dog-related service
          &mdash; including training (obedience, protection, behavior
          modification), boarding, daycare, grooming, dog walking,
          professional handling, or in-person consults &mdash; nor any of
          the following goods: human or veterinary prescription
          medications; live animals (use the dog marketplace); products
          containing CBD, hemp, kratom, or THC; raw or temperature-
          controlled meat shipped across state lines; any product
          requiring FDA, EPA, USDA, or DEA registration the seller does
          not hold; counterfeit, IP-infringing, or unauthorized brand
          merchandise; firearms, ammunition, training shock devices not
          legal in the seller&rsquo;s state; or any other category
          prohibited by Stripe&rsquo;s Restricted Businesses policy.
          Sellers offering services must operate through a separately-
          incorporated entity with its own general-liability and
          professional-liability coverage &mdash; the Registry will
          neither host nor process payment for those engagements.
        </li>
        <li>
          <strong>Storefront Seller Agreement.</strong> When a Seller claims a
          slug, they accept the separate{" "}
          <Link to="/storefront-seller-agreement">Storefront Seller Agreement</Link>,
          which is incorporated into these Terms by reference.
        </li>
        <li>
          <strong>Suspension and removal.</strong> The Registry may suspend,
          delist, or permanently remove any Storefront &mdash; with or without
          prior notice and in its sole discretion &mdash; for any violation of
          these Terms, the Storefront Seller Agreement, applicable law, the
          Stripe Restricted Businesses policy, or for repeated buyer
          complaints, chargebacks, or quality issues. Pending orders on a
          suspended Storefront are cancelled and refunded by the Seller in
          coordination with Stripe; the Registry will pause Stripe payouts to
          the affected account during suspension. Buyers&rsquo; order history
          remains visible to them for record-keeping.
        </li>
        <li>
          <strong>Featured rotation.</strong> The optional $29/month
          &ldquo;Featured&rdquo; slot is purely a promotional placement and
          does not confer any additional Registry endorsement, warranty, or
          quality guarantee of the Seller or their products.
        </li>
      </ul>

      <h2>5c. Acknowledgment for Storefront buyers</h2>
      <p>
        By placing an order on a Storefront page (
        <code>/store/&#123;slug&#125;</code>), you acknowledge and agree that:
      </p>
      <ul>
        <li>
          You are entering a contract with the Storefront Seller, not the
          Registry.
        </li>
        <li>
          The Registry is acting solely as a technology and payments
          facilitator and disclaims all responsibility for the product, its
          fitness for your animal, delivery, refunds, and any consequential
          harm (including illness, allergic reaction, injury, or property
          damage).
        </li>
        <li>
          You will direct any product, delivery, or refund issue to the
          Storefront Seller via the contact information shown on their
          Storefront page.
        </li>
        <li>
          You will not file a chargeback against the Registry for a Storefront
          transaction; chargebacks must be raised against the Storefront
          Seller through your card issuer.
        </li>
      </ul>

      <h2>5d. Upcoming Litter listings &mdash; venue role &amp; waitlist deposits</h2>
      <p>
        Through the Upcoming Litters product (
        <Link to="/marketplace/upcoming">/marketplace/upcoming</Link>) breeders
        may publish planned breedings and expected litters before the puppies
        are born, and buyers may pay a deposit to reserve a spot on the
        breeder&rsquo;s waitlist. The Registry&rsquo;s role in these
        transactions is strictly that of a <strong>venue and a payments
        facilitator</strong>. By creating, viewing, or transacting on an
        Upcoming Litter listing you acknowledge and agree that:
      </p>
      <ul>
        <li>
          <strong>The Registry is NOT a party</strong> to the breeder&rsquo;s
          sales contract, refund policy, health guarantee, or any other
          agreement between buyer and breeder. The Registry is not the
          breeder, buyer, guarantor, insurer, or legal counsel for either
          side.
        </li>
        <li>
          <strong>The contract is the breeder&rsquo;s contract.</strong> Each
          breeder uploads their own sales contract, which they are solely
          responsible for drafting and having reviewed by their own
          attorney. The Registry does not draft, review, edit, or guarantee
          the content of any contract.
        </li>
        <li>
          <strong>Deposits flow directly to the breeder.</strong> Waitlist
          deposits are charged via Stripe Connect destination charges and
          settle directly into the breeder&rsquo;s Stripe Connect account.
          The breeder is the <strong>merchant of record</strong> for the
          deposit. The Registry takes a platform fee (5%, capped at $25 per
          deposit) at the time of charge.
        </li>
        <li>
          <strong>Refund policy is the breeder&rsquo;s.</strong> Each
          listing displays the breeder&rsquo;s own refund policy verbatim.
          That policy is the controlling agreement between buyer and
          breeder. The Registry never auto-refunds a deposit; the
          &ldquo;Request refund&rdquo; button notifies the breeder but
          does not move money.
        </li>
        <li>
          <strong>Chargebacks land on the breeder.</strong> Any Stripe
          dispute on a deposit is assessed against the breeder&rsquo;s
          Stripe Connect account, not the Registry. The buyer&rsquo;s
          recourse for an unresolved deposit dispute is a Stripe
          dispute against the breeder (their merchant), not a
          chargeback against the Registry.
        </li>
        <li>
          <strong>Mandatory click-wrap acknowledgments.</strong> Both
          breeder and buyer must acknowledge a venue-disclaimer
          click-wrap (with name, IP, and timestamp captured in the
          audit log) before publishing a listing, paying a deposit,
          and signing the contract.
        </li>
        <li>
          <strong>8-week minimum age rule.</strong> The earliest
          go-home date on every Upcoming Litter is automatically set
          to 56 days after the recorded whelp date. The Registry
          enforces this floor for marketplace listings and escrow
          checkouts that flow through its platform.
        </li>
      </ul>

      <h2>5e. Armstrong Transport marketplace &mdash; independent transporters</h2>
      <p>
        The Armstrong Transport marketplace is a listing service connecting
        buyers with independent, USDA-licensed third-party transporters via a
        bidding engine and Stripe-Connect-backed escrow. This section governs
        the relationship between the Registry, the transporter, and the buyer
        for any trip booked through <Link to="/transport">/transport</Link>.
        The Registry is the marketplace operator, not the carrier &mdash; every
        trip is operated by an independent transporter under their own USDA
        license and commercial insurance.
      </p>
      <ul>
        <li>
          <strong>Verification floor.</strong> Every transporter listed in the
          public directory must (a) hold a current USDA Class B transport
          license, (b) carry a minimum of $300,000 in commercial-auto
          liability with proof on file, and (c) complete Stripe Connect
          Express onboarding with payouts enabled. The Registry reverifies
          insurance status quarterly.
        </li>
        <li>
          <strong>Bidding &amp; buyer choice.</strong> Buyers submit a route
          and the request is fanned out to every active transporter whose
          service-area-states cover either endpoint. Transporters submit bids;
          the buyer selects the winning bid and pays via Stripe. The Registry
          takes a flat 10% platform fee on every completed trip.
        </li>
        <li>
          <strong>Escrow &amp; auto-release.</strong> Buyer funds are held in
          Stripe escrow via a destination charge to the transporter&rsquo;s
          Connect account. Escrow auto-releases to the transporter 48 hours
          after the transporter marks the trip &ldquo;delivered,&rdquo; unless
          the buyer confirms earlier, opens a dispute, or the transporter has
          an active delay flag on the order.
        </li>
        <li>
          <strong>Cancellations &amp; disputes.</strong> Refund tiers,
          transporter-side cancellation rules, the dispute mediation process,
          and delay-flag behavior are governed by Section 9 of the{" "}
          <Link to="/refund">Refund Policy</Link>. Both parties accept
          those terms by booking or accepting a trip.
        </li>
        <li>
          <strong>Strike system.</strong> Transporters who cancel with less
          than 12 hours of notice (or where the Registry cannot rebook the
          trip with another verified transporter) receive one strike. Three
          strikes within a rolling 90-day window result in a 30-day suspension
          under the Transporter Agreement. The Registry may also issue strikes
          for repeated late deliveries, undisclosed subcontracting, or
          insurance lapses.
        </li>
        <li>
          <strong>Independent contractor relationship.</strong> Transporters
          are independent contractors operating their own ground transport
          businesses. They are not employees, agents, or partners of the
          Registry. The Registry is solely a technology platform and escrow
          facilitator. The Registry does not select routes, set prices,
          supervise driving, or warrant the condition of the
          transporter&rsquo;s vehicle. All transportation services are
          provided by the transporter under their own USDA license and
          commercial insurance.
        </li>
        <li>
          <strong>Sales &amp; use tax.</strong> Transport services are
          generally not subject to sales tax in most U.S. jurisdictions, but
          where applicable, the transporter &mdash; not the Registry &mdash;
          is the merchant of record and is solely responsible for collecting
          and remitting any tax due. The buyer pays the bid amount as quoted;
          no additional tax is collected by the Registry.
        </li>
        <li>
          <strong>Animal handling.</strong> Transporters agree to follow USDA
          Animal Welfare Act minimum standards for ground transport (rest
          stops every 4&ndash;6 hours, climate control, secure crates,
          documented water access). Reported failures to meet these standards
          trigger dispute escalation.
        </li>
        <li>
          <strong>Acknowledgment.</strong> By submitting a transport request
          or accepting a bid invitation, both buyer and transporter
          acknowledge they have read and accept this Section 5d and Section 9
          of the Refund Policy.
        </li>
      </ul>

      <h2 id="service-provider-bookings">5f. Service-provider bookings (trainers, groomers, walkers, sitters) &mdash; independent businesses; customer due diligence</h2>
      <p>
        Armstrong K9 Registry lists independent service providers
        &mdash; dog trainers, groomers, walkers, and sitters &mdash; so
        Customers can find, book, and pay them through the Platform.
        This section governs the relationship between the Registry, the
        Provider, and the Customer for any booking made through the
        directory or any provider profile. The Registry is the
        marketplace operator only. <strong>Every service is performed
        by an independent third-party business under their own license,
        insurance, certifications, and operating standards &mdash; not
        by Armstrong K9 Registry.</strong>
      </p>
      <ul>
        <li>
          <strong>You contract directly with the Provider.</strong>{" "}
          When you book a trainer, groomer, walker, or sitter, you are
          entering a service contract <em>directly with that Provider</em>.
          Armstrong is not a party to that contract, does not perform
          the service, does not supervise its performance, and does not
          adjudicate disputes between you and the Provider on the
          merits. Armstrong administers the booking record, the
          messaging thread, the Stripe-Connect escrow rails, and the
          published Refund-Policy timer &mdash; that is the full scope of
          our role.
        </li>
        <li>
          <strong>Customer due diligence is your responsibility.</strong>{" "}
          Armstrong does <em>not</em> verify, audit, certify, screen,
          or warrant any Provider&rsquo;s skill, training methods,
          handling practices, business license, insurance, criminal
          history, immigration status, vaccinations, vehicle condition,
          home conditions, or compliance with any state, county, or
          city law. Every Provider self-attests to operating as an
          independent business and self-answers a short licensing
          checklist at signup; the answers are the Provider&rsquo;s
          representations, not Armstrong&rsquo;s verifications.{" "}
          <strong>Before you book, you are expected to review the
          Provider&rsquo;s profile, photos, reviews, displayed
          certifications, claimed insurance, refund and cancellation
          policies, intake-form requirements, and any other public
          information &mdash; and to ask the Provider directly any
          questions material to your decision.</strong> Optional
          background-check badges (where displayed) are sold by
          third-party vendors and are informational only; their
          presence is not Armstrong&rsquo;s endorsement and their
          absence is not Armstrong&rsquo;s disqualification.
        </li>
        <li>
          <strong>You book at your own risk.</strong> Dog care
          inherently carries risk &mdash; bites, scratches, allergic
          reactions to grooming products, behavior regression during
          training, escape during walks, injury or illness during
          overnight sits, property damage, and other harms can occur
          even with a careful Provider. By booking a service through
          the Platform, <strong>you accept that risk and you accept
          that any resulting claim is between you and the Provider
          (and the Provider&rsquo;s insurer, if any) &mdash; not
          between you and Armstrong.</strong> Armstrong has no duty
          to inspect, supervise, or rescue any animal during a
          booking, and assumes no such duty by providing the booking
          platform.
        </li>
        <li>
          <strong>Per-booking venue acknowledgment.</strong> Every
          service booking requires you to check a box affirming you
          understand Armstrong&rsquo;s venue-only role and that you
          are contracting directly with the Provider. The Platform
          records your IP, timestamp, and the version of these Terms
          in force at the moment of acceptance. You may not complete
          a booking without that acknowledgment.
        </li>
        <li>
          <strong>Recommended Provider practices are not Armstrong
          requirements.</strong> Armstrong recommends that every
          Provider carry general-liability and animal-bailee insurance,
          a pet-first-aid certification, a third-party background
          check, and a written intake form covering vaccinations,
          medical conditions, and emergency-veterinary authorization.
          Armstrong does <em>not</em> require any of those items.
          Their presence or absence on a Provider profile is
          informational only and does not shift any duty to Armstrong.
        </li>
        <li>
          <strong>Escrow, refunds, and disputes.</strong> Service
          bookings are paid through Stripe Connect destination charges
          with platform-held escrow. Funds release to the Provider on
          your &ldquo;confirm completed&rdquo; click or on the
          auto-release deadline, whichever is earlier. Cancellation
          windows, no-show rules, refund tiers, and the
          dispute-resolution timer follow the published{" "}
          <Link to="/refund">Refund Policy</Link> in force at the
          time of booking. Disputes resolve either by mutual written
          consent between you and the Provider or, if no consent is
          reached within the default window, by the mechanical default
          stated in the Refund Policy. Armstrong administers the
          timer; <strong>Armstrong does not adjudicate the merits</strong>.
          Stripe chargebacks remain available as a parallel recourse
          under Stripe&rsquo;s own rules.
        </li>
        <li>
          <strong>Possession transfers to the Provider during the
          booking.</strong> When a Provider takes physical possession of
          your dog (every walk, every sit, most in-home trainings, most
          groomings), that Provider is solely responsible for your
          dog&rsquo;s safety during the booking. Live walk GPS, where
          offered, is a passive sharing tool described in Section
          5d-bis; it is not a supervision service and Armstrong does
          not monitor it.
        </li>
        <li>
          <strong>Ratings, reviews, and Provider responses</strong> are
          user-generated content. Armstrong does not adjudicate review
          accuracy. We remove reviews only where they violate the
          published Community &amp; Content rules (illegal content,
          threats, doxxing, etc.). Provider replies are limited to one
          written response per review.
        </li>
        <li>
          <strong>No employment, agency, or partnership relationship
          exists between Armstrong and any Provider.</strong> Every
          Provider operates their own independent business, sets their
          own prices and schedule, carries their own taxes and
          licenses, and accepts or declines bookings on their own
          judgment. Any claim asserting Armstrong is the Provider&rsquo;s
          employer, agent, principal, partner, joint venturer, or
          representative is contradicted by the Provider&rsquo;s written
          acknowledgment in the Service Provider Agreement and by this
          Section.
        </li>
        <li>
          <strong>Armstrong&rsquo;s liability for Provider conduct is
          zero.</strong> Subject only to Section 11 of these Terms and
          to any non-waivable consumer-protection law in your
          jurisdiction, <strong>Armstrong is not liable for any
          injury, illness, escape, bite, behavior regression,
          allergic reaction, death, theft, property damage, financial
          loss, emotional distress, or other harm arising out of any
          service booked through the Platform.</strong> Your remedy
          for any such harm is against the Provider directly (and the
          Provider&rsquo;s insurer, if any). The Provider has
          contractually agreed to indemnify Armstrong for any such
          claim under the Service Provider Agreement.
        </li>
        <li>
          <strong>Acknowledgment.</strong> By creating a booking
          through any service-provider profile or directory listing,
          you acknowledge you have read and accept this Section 5f,
          the per-booking venue acknowledgment described above, and
          the booking cancellation and dispute provisions of the
          Refund Policy in force at the time.
        </li>
      </ul>

      <h2 id="provider-client-messaging">5f-bis. Provider &harr; Client direct messaging &mdash; acceptable-use &amp; moderation</h2>
      <p>
        The in-app messaging surface between service providers and
        clients (the "Provider Messaging Channel") is provided to keep
        service-delivery details on-platform. Use of the channel is
        subject to these rules:
      </p>
      <ul>
        <li>
          <strong>Acceptable use.</strong> You will not use the Provider
          Messaging Channel to send (i)&nbsp;illegal content,
          (ii)&nbsp;sexually explicit material, (iii)&nbsp;harassment,
          threats, hate speech, or discriminatory remarks,
          (iv)&nbsp;fraudulent solicitations, phishing links, or
          malware, (v)&nbsp;bulk advertising or spam, or (vi)&nbsp;any
          attempt to circumvent Armstrong&rsquo;s escrow, refund, or
          dispute-resolution flows.
        </li>
        <li>
          <strong>Venue role; no editorial review.</strong> Armstrong
          K9 Registry is a venue. We do not pre-screen messages and we
          make no representation about the truth, accuracy, or quality
          of anything communicated through the channel. We do, however,
          reserve the right (but not the obligation) to:
          (a)&nbsp;quarantine a thread that has been reported via the
          in-app report flow, (b)&nbsp;remove specific messages or
          attachments that violate this section, (c)&nbsp;suspend the
          sending or receiving account in serious cases, and
          (d)&nbsp;preserve threads connected to an open dispute or a
          valid legal demand. We do not edit message bodies;
          enforcement is deletion-of-message-only.
        </li>
        <li>
          <strong>Contact-info detection.</strong> When you compose a
          message, the Registry performs a local-only regular-
          expression scan that flags US-format phone numbers and email
          addresses, and shows you a soft warning encouraging you to
          keep the conversation on the platform. The warning is
          informational and does not block you from sending. The
          presence of contact info shared in a message does not, by
          itself, violate these Terms.
        </li>
        <li>
          <strong>Off-platform contact is at your own risk.</strong> If
          you choose to share contact information and continue a
          conversation off Armstrong, the Registry cannot assist with
          disputes, cancellations, refunds, or safety incidents
          relating to anything arranged through that off-platform
          channel. Keeping conversations in-app preserves your paper
          trail.
        </li>
        <li>
          <strong>No professional advice.</strong> Messages exchanged
          with a service provider are between you and that independent
          business. Armstrong does not endorse, vet, or warrant any
          veterinary, training, dietary, behavioral, or legal advice
          provided by a service provider via the channel. If a
          provider&rsquo;s message recommends a medication, surgery,
          treatment, or other intervention, confirm with a licensed
          veterinarian before acting.
        </li>
        <li>
          <strong>Storage &amp; retention.</strong> Messages, photo
          attachments, and system events are stored, retained, and
          purged per the schedule published in our{" "}
          <Link to="/legal/privacy#section-2-4a">Privacy Policy &sect;2.4a</Link>.
        </li>
      </ul>

      <h2 id="trainer-learning">5f-ter. Trainer Learning &mdash; live video sessions &amp; pre-recorded courses</h2>
      <p>
        Approved Trainer and Behaviorist providers may offer two paid
        learning products through the Platform: (a) <strong>live 1-on-1
        video training sessions</strong> delivered through Armstrong&rsquo;s
        Daily.co-powered video rooms; and (b) <strong>pre-recorded video
        courses</strong> sold as a flat-fee one-time purchase, included
        with the Buyer Pass, or both. This section governs the rights
        and responsibilities of the Trainer, the Customer, and the
        Registry for any such purchase.
      </p>
      <ul>
        <li>
          <strong>Independent service relationship.</strong> Live
          sessions and pre-recorded courses are delivered by the
          independent Trainer, not by Armstrong K9 Registry. Armstrong
          provides the booking system, the Daily.co room provisioning,
          the Stripe-Connect payment rails, the lesson hosting, and the
          Customer &harr; Trainer message thread &mdash; that is the
          full scope of our role. Section 5f (independent-business,
          venue-only, customer-due-diligence, at-your-own-risk) applies
          to every Trainer-Learning purchase in addition to the rules
          in this Section 5f-ter.
        </li>
        <li>
          <strong>Live sessions &mdash; pricing &amp; payout.</strong>{" "}
          Trainers publish up to three packaged tiers (e.g. 1-session,
          5-pack, 10-pack) and a weekly availability map. Customers
          book a tier at a specific time slot; payment is captured
          immediately via Stripe Connect destination charge. Funds are
          routed to the Trainer&rsquo;s connected Stripe Express
          account net of the platform fee defined below. Payouts
          follow Stripe&rsquo;s standard schedule for the Trainer&rsquo;s
          account. The Trainer is solely responsible for completing
          Stripe Connect onboarding (KYC, bank verification, tax
          identification) and for any taxes owed on session revenue;
          Stripe issues the Trainer&rsquo;s 1099-K directly from the
          Trainer&rsquo;s Express dashboard. Armstrong does not file
          1099s on behalf of Trainers.
        </li>
        <li>
          <strong>Pre-recorded courses &mdash; pricing &amp; payout.</strong>{" "}
          Trainers may sell courses on a <em>flat-fee</em> basis (buyer
          owns lifetime access), a <em>Buyer-Pass-included</em> basis
          (buyer watches at no incremental cost; Trainer earns $0.40
          royalty per qualifying ≥30-second lesson watch), or both.
          Flat-fee purchases settle the same way as live sessions
          (Stripe Connect destination charge, platform fee skimmed,
          balance to the Trainer&rsquo;s connected account).
          Buyer-Pass royalty pools are calculated monthly and paid out
          via the same connected account.
        </li>
        <li>
          <strong>Platform fee (both products).</strong> Armstrong
          retains a tiered platform fee on every Trainer-Learning
          transaction: <strong>20% on the first $5,000 of trailing-12-month
          Trainer-Learning gross per Trainer, then 10% thereafter.</strong>{" "}
          The fee is calculated and disclosed inside Stripe&rsquo;s
          <code>application_fee_amount</code> on every charge and is
          visible to the Trainer inside their Express dashboard. The fee
          is independent of and additional to Stripe&rsquo;s own
          processing fees, which Stripe deducts before settlement.
        </li>
        <li>
          <strong>Recording &amp; replay (live sessions).</strong> The
          Daily.co room is auto-configured to record the session. The
          replay URL is shared with both the Trainer and the Customer
          and remains accessible for <strong>30 days</strong> from the
          end of the session, after which it is permanently deleted.
          Customers consent to being recorded by joining the room;
          Trainers consent by enabling their offering. Either party
          may end the recording mid-session from inside Daily.co&rsquo;s
          controls. Recordings are not used for any purpose other than
          buyer/Trainer replay and dispute resolution; they are not
          training data, are not shared with third parties, and are
          deleted on schedule.
        </li>
        <li>
          <strong>No medical, veterinary, or behaviorist advice.</strong>{" "}
          Training-session content and course content are educational
          and behavioural in nature. They are <em>not</em> veterinary
          advice, diagnosis, or treatment, and they do not establish a
          veterinarian-patient relationship. Customers should consult a
          licensed veterinarian for any medical concern. Trainers may
          not represent themselves as veterinarians, may not prescribe
          medications or veterinary treatments, and may not promise
          specific behavioural outcomes.
        </li>
        <li>
          <strong>Cancellation &amp; refunds (live sessions).</strong>{" "}
          Each Trainer publishes a cancellation window (default 24
          hours). A Customer who cancels at least the published window
          before the scheduled start receives a full refund. Inside the
          window, the Trainer&rsquo;s published policy applies; in the
          absence of a published policy, the booking is non-refundable
          but may be rescheduled at the Trainer&rsquo;s discretion.
          No-shows by the Customer forfeit the session. No-shows by the
          Trainer entitle the Customer to a full refund or a free
          reschedule, at the Customer&rsquo;s option.
        </li>
        <li>
          <strong>Refunds (courses).</strong> Flat-fee course purchases
          are eligible for a full refund within 14 days <em>only if
          fewer than 30% of lesson minutes have been watched</em>, as
          measured by the platform&rsquo;s lesson-watch counter. After
          14 days or after the 30% threshold, course purchases are
          final. Buyer-Pass-included courses follow the underlying
          Buyer-Pass refund schedule; cancelling the Buyer Pass ends
          access to Buyer-Pass-included courses but does not refund the
          purchase price of any flat-fee course already bought
          separately.
        </li>
        <li>
          <strong>Content ownership &amp; license.</strong> Trainers
          retain ownership of all course content and live-session
          recordings they author. By publishing a course or running a
          live session through the Platform, the Trainer grants
          Armstrong a worldwide, non-exclusive, royalty-free license to
          host, stream, transcode, cache, back up, and deliver the
          content to authorized Customers for the duration the course
          remains published (or the 30-day replay window for live
          sessions). Armstrong does not claim ownership, does not
          remarket the content, and does not use Trainer content to
          train any model. Customers receive a personal,
          non-transferable, non-commercial license to watch purchased
          content; redistribution, public performance, derivative
          works, or downloading-for-resale are prohibited and may
          result in account termination.
        </li>
        <li>
          <strong>Trainer warranties.</strong> By publishing a course
          or running a session, the Trainer warrants that: (a) the
          content is original or properly licensed; (b) no third-party
          copyright, trademark, right of publicity, or other right is
          infringed; (c) all minors, third parties, and identifiable
          dogs depicted have given (or for minors, had a guardian give)
          consent to appear; (d) the Trainer holds any state, county,
          or city licenses required to deliver dog-training services in
          the jurisdictions in which they operate; and (e) the content
          complies with Section 8 (Acceptable Use) of these Terms.
          Armstrong removes content that violates these warranties on
          notice and reserves the right to suspend an offending
          Trainer&rsquo;s account.
        </li>
        <li>
          <strong>Disputes.</strong> Disputes over a live session
          (no-show, technical failure, content quality) or a course
          purchase (advertised vs. delivered, lesson accessibility,
          refund eligibility) are resolved first by direct conversation
          in the Customer &harr; Trainer message thread, and failing
          that by the mechanical default rules in this Section 5f-ter
          and in the published <Link to="/refund">Refund Policy</Link>.
          Armstrong administers the timer and the refund mechanics;
          Armstrong does not adjudicate the merits. Stripe chargebacks
          remain available as a parallel recourse.
        </li>
        <li>
          <strong>Armstrong liability is zero.</strong> Subject only to
          Section 11 of these Terms and any non-waivable
          consumer-protection law, Armstrong is not liable for: the
          quality, accuracy, or effectiveness of any session or course;
          any injury, regression, escape, bite, illness, or property
          damage occurring during or after a Trainer-Learning purchase;
          any outage of the Daily.co video infrastructure or the
          lesson-hosting infrastructure; or any tax, licensing, or
          professional-regulation consequence to the Trainer. The
          Trainer indemnifies Armstrong for any such claim under the
          Service Provider Agreement.
        </li>
        <li>
          <strong>Acknowledgment.</strong> By booking a live session
          or purchasing a course, the Customer acknowledges they have
          read and accept this Section 5f-ter, Section 5f (venue
          role), and the refund and recording provisions above.
        </li>
      </ul>

      <h2 id="gps-live-walk">5d-bis. Live walk GPS tracking &mdash; optional, in-app only, walker-initiated</h2>
      <p>
        Service providers who walk or sit your dog may, with your prior
        consent on the booking, share a live location map during the
        active session. The Registry&rsquo;s role with respect to this
        feature is strictly limited:
      </p>
      <ul>
        <li>
          <strong>Walker initiates.</strong> The provider voluntarily
          enables sharing at the start of each walk. The owner cannot
          force a provider to share location. Providers without GPS or
          who decline to share are not penalized in ranking.
        </li>
        <li>
          <strong>Bilateral consent.</strong> Both parties must opt in
          before any location data is transmitted: the owner enables it
          on the booking, and the provider initiates sharing for that
          specific walk. Either party may revoke at any moment from
          inside the app.
        </li>
        <li>
          <strong>In-app only.</strong> There is no public, no-login
          share link. The owner views the live map exclusively inside
          their authenticated <code>/my-bookings</code> page. Location
          data never traverses email, SMS, or any out-of-band channel.
        </li>
        <li>
          <strong>No long-term storage.</strong> The Registry does NOT
          retain the full route. When the walk ends or is revoked, the
          raw GPS points are immediately discarded and replaced with a
          24-hour summary (distance, duration, approximate start and end
          locations, snapped to roughly the nearest 110 metres for
          home-address obfuscation). After 24 hours the entire record is
          permanently deleted.
        </li>
        <li>
          <strong>No emergency or safety guarantees.</strong> The
          Registry transmits location data as a courtesy between
          consenting parties. It does NOT monitor walks, alert anyone
          if the walker stops moving, alert anyone if the walker leaves
          a geofence, verify the route, or guarantee accuracy or
          continuity of the GPS signal. <strong>The feature is a dumb
          pipe between two consenting users; it is not a safety
          system.</strong>
        </li>
        <li>
          <strong>Provider independence preserved.</strong> Nothing in
          this feature alters the independent-contractor status of any
          provider. The Registry does not direct, supervise, monitor,
          schedule, or control any walk. Providers are not employees of
          the Registry and are free to walk without enabling sharing.
        </li>
        <li>
          <strong>You assume the risk.</strong> By enabling live walk
          tracking, both the owner and the provider acknowledge that
          the Registry is a venue and that any reliance on the live
          location signal is at the user&rsquo;s own risk. The
          Registry disclaims any duty to monitor, intervene, or
          otherwise act on location data exchanged through this
          feature.
        </li>
      </ul>

      <h2>5e. Calendar predictions, gestation estimates &amp; reminder notifications</h2>
      <p>
        The Registry includes a personal calendar that displays user-entered
        events alongside automatically derived events (heat-cycle dates,
        breeding-to-whelping projections, transport pickups, video-tour
        bookings, dog birthdays). Some of these are statistical estimates, not
        guarantees.
      </p>
      <ul>
        <li>
          <strong>Predicted whelp dates</strong> are calculated as breeding
          date + 63 days, the average canine gestation. Actual gestation ranges
          from approximately 58 to 68 days and varies by breed, dam, and
          individual pregnancy. The Registry, its operators, and its officers
          are not responsible for the accuracy of any predicted whelp date and
          make no warranty, express or implied, that puppies will be born on
          or near the predicted date.
        </li>
        <li>
          <strong>Predicted next-heat dates</strong> are calculated as
          most-recent-recorded-heat + 180 days, the rough average heat
          interval. Actual heat cycles vary widely by breed and individual. We
          make no warranty as to accuracy.
        </li>
        <li>
          <strong>Tentative events</strong> are clearly labeled in italics
          with a &ldquo;Tentative&rdquo; badge and an in-app disclaimer at
          creation time. You acknowledge that these are estimates only and
          that any decision you make in reliance on them &mdash; including,
          without limitation, scheduling travel, accepting buyer deposits,
          booking veterinary appointments, or making other commitments &mdash;
          is at your sole risk.
        </li>
        <li>
          <strong>Confirm with a licensed veterinarian.</strong> For any
          breeding decision the Registry recommends ultrasound or progesterone
          testing performed by a licensed veterinarian. The Registry&rsquo;s
          calendar features are not veterinary advice and are not a substitute
          for professional veterinary care.
        </li>
        <li>
          <strong>Reminder notifications are best-effort.</strong> The
          Registry may send web push notifications approximately 24 hours
          before scheduled events to devices you have enrolled. Delivery
          depends on browser availability, network conditions, operating
          system push services (Apple Push, FCM, Mozilla autopush), and your
          device&rsquo;s battery and notification settings. We do not
          guarantee delivery and recommend you do not rely on these
          notifications as your sole reminder for time-critical events.
        </li>
      </ul>

      <h2>Editorial content &mdash; Journal, puppy school, and lifecycle emails</h2>
      <p>
        The Registry publishes long-form editorial content in the public{" "}
        <em>Journal</em> at <code>/journal</code> and delivers a multi-day{" "}
        <em>first-puppy school</em> drip course by email (days 1, 3, 5, 7,
        14, and 30 after a dog transfer). The Registry also sends annual
        and one-time lifecycle emails about dogs you own (birthday,
        Gotcha Day anniversary, 30-day buyer check-in).
      </p>
      <ul>
        <li>
          <strong>Educational only.</strong> All editorial and lifecycle
          content is general information for adult dog owners and
          prospective buyers. It is <em>not</em> veterinary, legal, tax,
          training, or financial advice and is not a substitute for
          consultation with a licensed professional.
        </li>
        <li>
          <strong>No professional relationship.</strong> Reading or
          receiving this content does not create a veterinarian-client,
          attorney-client, accountant-client, or trainer-client
          relationship between you and Armstrong K9 Registry, its
          editorial team, or any third party referenced therein.
        </li>
        <li>
          <strong>Health, breeding, and emergency decisions</strong>{" "}
          &mdash; including but not limited to vaccination schedules,
          medication dosing, diagnosis or treatment of any condition,
          dietary changes, anesthetic events, or breeding decisions{" "}
          &mdash; must be made in consultation with a licensed
          veterinarian familiar with your specific dog. The Registry
          disclaims any responsibility for outcomes arising from
          decisions made in reliance on Journal or puppy-school
          content.
        </li>
        <li>
          <strong>Trademark policy.</strong> The Registry does not
          reference, claim affiliation with, or operate under the name
          of any third-party kennel club or registry. All editorial
          standards described in the Journal reflect{" "}
          <em>this platform&rsquo;s</em> internal verification process
          and are not industry-wide standards.
        </li>
        <li>
          <strong>Featured kennels, breeders, and buyers</strong>{" "}
          appearing in editorial content do so with their consent.
          Photos and quotes are used with permission and may be
          retracted on request by emailing the editorial team.
        </li>
        <li>
          <strong>Lifecycle email opt-out.</strong> Every lifecycle
          email contains a one-click unsubscribe link compliant with
          CAN-SPAM Section 5(a)(3). You may also manage these emails
          from Settings &rarr; Notifications (global) or each
          dog&rsquo;s Ownership tab (per-dog). Transactional emails
          required to operate the service (purchases, password resets,
          transfer requests, certificate delivery) cannot be
          unsubscribed from.
        </li>
        <li>
          <strong>Day-30 review nudge.</strong> The 30-day buyer
          check-in email invites you to leave a verified review of the
          breeder you purchased from. Reviews are voluntary and never
          incentivized; the Registry does not filter or remove reviews
          based on sentiment. If you believe a transaction went poorly,
          you may instead contact our Trust &amp; Safety team and we
          will investigate.
        </li>
      </ul>

      <h2>5f. AI-assisted features &amp; AI-generated content</h2>
      <p>
        Certain admin-facing and user-facing features use third-party large
        language models (LLMs) &mdash; currently Anthropic&rsquo;s Claude
        &mdash; to generate text on demand. These include the AI
        marketing-pack generator (admin only), the kennel-bio assistant, and
        any other feature labeled &ldquo;AI&rdquo; or
        &ldquo;AI-generated.&rdquo;
      </p>
      <ul>
        <li>
          <strong>Output is probabilistic, not authoritative.</strong> AI-
          generated text may contain factual errors, omissions, or invented
          details. You are responsible for reviewing any AI-generated content
          before publishing it under your name or using it in contracts,
          listings, or public communications.
        </li>
        <li>
          <strong>Where prompts go.</strong> When you trigger an AI feature we
          send the prompt and any context required to fulfill it (e.g., live
          platform statistics for the marketing generator, your kennel
          description for the bio assistant) to the LLM provider. We do not
          send other users&rsquo; personal data to the LLM unless you
          explicitly include it in your prompt. The LLM provider&rsquo;s
          data-handling terms apply; see the{" "}
          <Link to="/sub-processors">Sub-processors</Link> page for the
          current list and DPA links.
        </li>
        <li>
          <strong>No training on your content.</strong> Our LLM access is
          configured so that prompts and outputs are not retained for
          model-training purposes by the provider. If a provider&rsquo;s terms
          ever change, we will update the Sub-processors page and notify you
          before any retention change takes effect.
        </li>
        <li>
          <strong>You own AI-generated outputs you keep.</strong> Subject to
          the LLM provider&rsquo;s terms, content the AI generates for you
          becomes part of your account content under Section 7. You may edit,
          publish, or discard it as you choose.
        </li>
      </ul>

      <h2 id="zoomie-time" data-testid="terms-zoomie-time-section">
        5f-quater. Zoomie Time &mdash; private field rentals
      </h2>
      <p>
        Zoomie Time lets independent landowners (&ldquo;Hosts&rdquo;)
        rent their private fields, yards, or acreage to Customers by
        the hour for off-leash dog exercise. Every Zoomie Time listing
        is an independent offering by the Host. <strong>Armstrong K9
        Registry is the marketplace operator only; it does not own,
        inspect, maintain, or supervise any field.</strong> The
        independent-business, direct-contract, payments, and
        limitation-of-liability rules of Section 5f apply to Zoomie
        Time bookings, supplemented by this Section 5f-quater.
      </p>
      <ul>
        <li>
          <strong>Host self-reported disclosures.</strong> Fencing
          status (fully fenced, partially fenced, unfenced), acreage,
          amenities, and photos are provided by the Host and are{" "}
          <em>not verified by the Registry</em>. You are solely
          responsible for judging whether a field is suitable and
          secure for your dogs before and during your visit.
        </li>
        <li>
          <strong>Mandatory liability waiver.</strong> Before
          completing any Zoomie Time booking you must accept the
          field-use liability waiver, in which you assume all risk of
          injury, escape, illness, parasite exposure, property damage,
          or dog-to-dog incidents arising from your use of the field,
          and release both the Host and Armstrong K9 Registry to the
          fullest extent permitted by law. Your acceptance (with
          timestamp) is recorded with the booking.
        </li>
        <li>
          <strong>Address privacy.</strong> A field&rsquo;s exact
          address is hidden from public listings and is revealed to
          you only after your booking is confirmed. You agree not to
          publish, share, or reuse a Host&rsquo;s address outside the
          booking, and not to visit the property outside your booked
          window.
        </li>
        <li>
          <strong>Registered dogs only.</strong> Zoomie Time bookings
          (like all service bookings on the Platform) must identify
          which of your registered dogs will attend. Only the dogs
          named on the booking may be brought to the field.
        </li>
        <li>
          <strong>Your conduct on the property.</strong> You must
          supervise your dogs at all times, pick up after them, keep
          them within the rented area, respect any Host house rules
          shown on the listing, and leave promptly at the end of your
          slot (buffer gaps between bookings exist so guests never
          overlap).
        </li>
        <li>
          <strong>Cancellations &amp; refunds.</strong> Zoomie Time
          bookings follow the cancellation terms shown at checkout and
          Section 5 of our Refund Policy. Weather does not
          automatically entitle you to a refund unless the Host
          cancels.
        </li>
        <li>
          <strong>Hosts.</strong> By listing a field you represent
          that you have the legal right to rent it, that your
          disclosures are accurate, that the field is reasonably safe
          for its described use, and that you maintain any insurance
          required in your jurisdiction. Hosts are independent
          businesses under Section 5f and receive payouts via Stripe
          Connect.
        </li>
      </ul>

      <h2>5g. Referral program</h2>
      <p>
        Every registered user can generate a unique referral code from
        the Share my kennel page. Anyone who creates an account using
        that code via <code>?ref=CODE</code> in the signup URL is
        attributed to the referrer for analytics, leaderboard, and any
        future credit-issuance purposes.
      </p>
      <ul>
        <li>
          Referral codes are personal to the issuing account; sharing or
          selling codes for compensation is prohibited and may result in
          code revocation.
        </li>
        <li>
          Self-referrals (signing up via your own code, alt accounts,
          household-sharing) do not count and may result in attribution
          being voided.
        </li>
        <li>
          The Registry reserves the right to revoke codes, void
          attributions, or close accounts engaged in fraudulent referral
          activity (e.g. fake signups, click-farming, paid sign-up
          schemes).
        </li>
        <li>
          Referral credit, where offered, has{" "}
          <strong>no cash value</strong> and cannot be transferred,
          sold, or combined with other promotional offers unless
          explicitly stated.
        </li>
        <li>
          The Registry may modify or end the referral program at any
          time with reasonable notice; accrued credit will be honored
          for 90 days after such notice.
        </li>
      </ul>

      <h2 id="mobile-apps">5h. Mobile apps &amp; app-store purchases (Apple App Store &middot; Google Play)</h2>
      <p>
        The Armstrong K9 Registry mobile application is distributed through
        the Apple App Store and Google Play. Your use of the app is also
        subject to the applicable store&rsquo;s own terms of service and
        usage rules, in addition to these Terms.
      </p>
      <h3>5h.1 Subscriptions purchased through Apple In-App Purchase</h3>
      <ul>
        <li>
          Subscriptions purchased inside the iOS app are processed by Apple
          as <strong>In-App Purchases billed to your Apple&nbsp;ID</strong>.
          Apple &mdash; not the Registry &mdash; is the merchant of record
          for those purchases, and pricing shown in the app is set on the
          App&nbsp;Store and may differ from web pricing.
        </li>
        <li>
          In-App Purchase subscriptions auto-renew until cancelled and{" "}
          <strong>can only be managed or cancelled through your Apple&nbsp;ID
          subscription settings</strong> (Settings &rarr; your name &rarr;
          Subscriptions on your device). They cannot be cancelled from our{" "}
          <Link to="/billing">Billing</Link> page.
        </li>
        <li>
          Refunds for In-App Purchases are handled exclusively by Apple under
          Apple&rsquo;s refund policies &mdash; see Section 2a of our{" "}
          <Link to="/refund">Refund Policy</Link>.
        </li>
        <li>
          Subscriptions purchased on our website are processed by Stripe and
          remain governed by Section 5 of these Terms and our Refund Policy.
          The same account never needs both: an active subscription from
          either source unlocks the same features everywhere.
        </li>
      </ul>
      <h3>5h.2 Apple &mdash; required acknowledgments</h3>
      <ul>
        <li>
          These Terms are an agreement between you and Armstrong K9 Registry
          only, <strong>not with Apple Inc.</strong> Apple is not responsible
          for the app or its content, and has no obligation to furnish any
          maintenance or support services for it.
        </li>
        <li>
          In the event the app fails to conform to any applicable warranty,
          you may notify Apple and Apple will refund the purchase price of
          the app (if any); to the maximum extent permitted by law, Apple has
          no other warranty obligation with respect to the app.
        </li>
        <li>
          Apple is not responsible for addressing any claims by you or a
          third party relating to the app, including product-liability
          claims, claims that the app fails to conform to legal or regulatory
          requirements, claims under consumer-protection or similar law, and
          claims of intellectual-property infringement.
        </li>
        <li>
          You represent that you are not located in a country subject to a
          U.S. government embargo or designated a &ldquo;terrorist
          supporting&rdquo; country, and that you are not on any U.S.
          government list of prohibited or restricted parties.
        </li>
        <li>
          <strong>Apple, and Apple&rsquo;s subsidiaries, are third-party
          beneficiaries of these Terms</strong> and, upon your acceptance,
          have the right (and are deemed to have accepted the right) to
          enforce these Terms against you as a third-party beneficiary.
        </li>
      </ul>
      <h3>5h.3 Google Play</h3>
      <ul>
        <li>
          The Android app is distributed under the Google Play Terms of
          Service. Google is not a party to these Terms and is not
          responsible for the app, its content, or any claims relating to
          it.
        </li>
        <li>
          Purchases initiated from the Android app are currently completed
          on our website in your browser and are processed by Stripe under
          Section 5 of these Terms.
        </li>
      </ul>

      <h2>5i. International sellers &mdash; direct payments (no Registry involvement)</h2>
      <p>
        Sellers and service providers located outside the United States
        (&ldquo;International Sellers&rdquo;) process every sale as a{" "}
        <strong>direct charge on their own Stripe account</strong>. For any
        purchase from an International Seller:
      </p>
      <ul>
        <li>
          <strong>The International Seller is the merchant of record</strong>{" "}
          &mdash; not Armstrong K9 Registry. The Registry never receives,
          holds, routes, escrows, or controls the funds; our platform fee is
          deducted automatically by Stripe from the seller&rsquo;s charge.
        </li>
        <li>
          <strong>Registry escrow and dispute protections do not apply.</strong>{" "}
          Refunds, disputes, and chargebacks are handled solely by the
          International Seller under their own policies and Stripe&rsquo;s
          dispute process. Your recourse is with the seller and your card
          issuer, not the Registry.
        </li>
        <li>
          Buyers are shown a prominent disclosure and must affirmatively
          acknowledge these terms before completing any purchase from an
          International Seller; the acknowledgment is recorded with the
          order. International Sellers likewise accept a recorded{" "}
          <strong>International Seller Payment Agreement</strong> before
          payment onboarding, confirming they alone bear responsibility for
          refunds, disputes, taxes (including VAT/GST), and legal compliance
          in their jurisdiction.
        </li>
        <li>
          Recurring booking packages and Registry escrow checkout are not
          available for International Sellers.
        </li>
      </ul>

      <h2>6. Acceptable use</h2>
      <ul>
        <li>No illegal activity, harassment, doxxing, or threats.</li>
        <li>No deceptive listings or pedigree fraud.</li>
        <li>
          No automated access, scraping, bulk extraction, data mining, or
          automated posting of any kind. Section 6a below governs automated
          access in detail and is a material term of these Terms.
        </li>
        <li>
          <strong>No content promoting cruelty, dog-fighting, or
          breeding/training for aggression toward humans.</strong>{" "}
          For clarity, the following are <em>permitted</em>: footage of
          sanctioned canine sport &amp; working trials (IPO/IGP,
          Schutzhund, PSA, Mondio Ring, French Ring, KNPV), AKC- or
          UKC-style working trials, hunt tests, retrieves, agility,
          dock-diving, herding, obedience, conformation movement, and
          similar sport disciplines, provided the work is sanctioned,
          handler-led, and depicts no cruelty or animal in distress.
          The following are <strong>prohibited</strong>: dog-fighting
          (depicting or promoting), untitled &ldquo;guard dog / personal
          protection dog&rdquo; advertising, prong/e-collar at illegal
          intensities, minors used as decoys without a visible
          guardian, or content depicting any activity illegal in the
          seller&rsquo;s jurisdiction.
        </li>
        <li>
          No reverse-engineering of the Registry, our payment integration, or
          admin tooling.
        </li>
      </ul>

      <h2 data-testid="terms-anti-scraping-section">
        6a. Automated access, scraping &amp; data mining &mdash; prohibited
      </h2>
      <p>
        The Registry&rsquo;s pedigree database, dog and litter records,
        kennel and breeder listings, service-provider directory, the Master
        Pedigree lineage graph, and the selection, structure, coordination,
        and arrangement of all Registry data are proprietary compilations
        owned by Armstrong K9 Registry and protected by copyright, database,
        and unfair-competition law. Your access to the Registry is a
        limited, revocable, non-transferable licence for your personal or
        internal business use only. Except as expressly permitted in
        writing by us, you must not, and must not enable or assist any
        third party to:
      </p>
      <ul>
        <li>
          Use any robot, spider, crawler, scraper, headless browser,
          browser extension, script, or other automated means to access,
          collect, copy, monitor, or index any part of the Registry or its
          data;
        </li>
        <li>
          Harvest or bulk-extract pedigree records, dog profiles, photos,
          contact information, listings, prices, reviews, or any other
          Registry data, whether by automated means or by systematic manual
          collection;
        </li>
        <li>
          Access or use our APIs other than through interfaces we document
          and expressly authorize, or exceed authorized use of any API;
        </li>
        <li>
          Circumvent, disable, or interfere with rate limits, CAPTCHA,
          authentication, robots.txt directives, IP blocks, or any other
          technical measure we use to protect the Registry;
        </li>
        <li>
          Use any Registry data or content to train, fine-tune, ground,
          augment, or otherwise develop any artificial-intelligence or
          machine-learning model, dataset, or system;
        </li>
        <li>
          Copy, resell, sublicense, republish, or redistribute Registry
          data, or use it to build or populate a competing registry,
          directory, database, or product; or
        </li>
        <li>
          Frame, mirror, or cache any part of the Registry for
          redistribution.
        </li>
      </ul>
      <p>
        <strong>Limited exception.</strong> Operators of general-purpose
        public search engines may use compliant crawlers to index publicly
        available pages solely to direct users to the Registry, provided
        they honor our robots.txt and do not bulk-redistribute Registry
        data. We may revoke this exception at any time.
      </p>
      <p>
        <strong>Enforcement.</strong> Violation of this Section 6a is a
        material breach. We may immediately suspend or terminate your
        account without refund, revoke your access licence, block your IP
        addresses and devices, and demand certification of destruction of
        any data obtained in violation of this Section. You agree that
        unauthorized automated access may also constitute copyright
        infringement, misappropriation, trespass to chattels, and a
        violation of the Computer Fraud and Abuse Act (18 U.S.C. &sect;
        1030) and California Penal Code &sect; 502, and that we may pursue
        all available civil remedies, including damages, recovery of our
        investigation and enforcement costs and reasonable
        attorneys&rsquo; fees where permitted by law. Because unauthorized
        extraction of Registry data causes harm that is difficult to
        quantify, you agree we are entitled to seek injunctive relief in
        addition to any other remedy, without the requirement of posting a
        bond.
      </p>

      <h2 data-testid="terms-member-messaging-section">6b. Member-to-member messaging (Registry Chat)</h2>
      <p>
        The Registry provides an optional member-to-member messaging feature
        (&ldquo;Registry Chat&rdquo;) as a convenience. By using it you agree:
      </p>
      <ul>
        <li>
          <strong>Opt-out anytime.</strong> Messaging is enabled by default
          for all accounts. You may disable it at any time in Messages
          &rarr; settings, after which other members cannot start new
          conversations with you or send you further messages.
        </li>
        <li>
          <strong>Reported conversations may be reviewed.</strong> When
          either participant reports a conversation, Registry staff may
          read its full contents for trust-and-safety review. Outside of
          a report, staff do not access private conversations, except as
          required by law.
        </li>
        <li>
          <strong>Automated safety scanning.</strong> Messages are scanned
          automatically for patterns associated with scams and off-platform
          payment solicitation (for example wire transfers or gift cards),
          and warnings may be displayed. We do not guarantee detection of
          any harmful content, and a warning (or its absence) is not an
          endorsement or verification of any member or statement.
        </li>
        <li>
          <strong>Prohibited conduct.</strong> Harassment, spam, unlawful
          content, and soliciting payment outside the Registry to evade
          buyer protections are prohibited and may result in loss of
          messaging privileges or account termination under Section 9.
        </li>
        <li>
          <strong>Blocks.</strong> Either participant may block the other
          at any time; blocked members cannot start conversations or send
          messages in either direction.
        </li>
        <li>
          <strong>Venue role unchanged.</strong> Messages are user-generated
          content under Section 7a. The Registry is not a party to, and is
          not responsible for, any statement, offer, or agreement made in
          chat. Any transaction arranged through Registry Chat remains
          subject to the disclaimers and payment terms in Sections 3&ndash;5,
          and the Registry never processes or guarantees payments arranged
          privately between members.
        </li>
      </ul>

      <h2>7. Content ownership</h2>
      <ul>
        <li>You retain ownership of content you upload.</li>
        <li>
          You grant the Registry a non-exclusive, worldwide, royalty-free
          licence to display, reproduce, and distribute it in connection with
          operating the platform &mdash; including SEO surfaces, social-share
          previews, and search results.
        </li>
        <li>
          The Registry brand, layout, code, and database structure are owned
          by Armstrong K9 Registry and protected by copyright.
        </li>
      </ul>

      <h2>7a. User-generated content &middot; review responses &middot; hero video &middot; kennel placement</h2>
      <ul>
        <li>
          <strong>Right-of-reply on reviews.</strong> Verified buyer reviews
          may be responded to once by the reviewed kennel. You are solely
          responsible for the content of your response. The Registry is a
          neutral host and does not endorse the content of any review or
          response. We may remove a response that contains defamation,
          threats, harassment, personal identifying information about the
          reviewer, or any other Acceptable-Use violation. Responses are
          subject to the same Trust &amp; Safety reporting flow as any
          other content.
        </li>
        <li>
          <strong>Hero video (Kennel Elite).</strong> By uploading a hero
          video to your public kennel page you represent and warrant that
          you own or have all necessary rights to the video, audio, and any
          identifying likeness depicted, and that the content complies with
          all applicable laws and the Acceptable-Use policy. You grant the
          Registry the licence described in &sect;7 for that video. The
          Registry may remove a hero video that uses copyrighted music
          without licence, depicts people who have not consented, or
          otherwise violates these terms. DMCA takedowns are handled per
          &sect;8.
        </li>
        <li id="working-video">
          <strong>Working / sport video on dog profiles.</strong> The
          Registry lets verified sellers attach up to three (3) short
          video clips to each individual dog profile so buyers can see
          drive, structure, or conformation movement before committing
          to a litter. By uploading a video to a dog profile, you
          additionally represent and warrant that:
          <ul>
            <li>
              the video depicts sanctioned canine sport or training as
              described in &sect;6 (or routine handling such as
              free-stacking, gaiting, or daily-life behavior) and does
              <strong>not</strong> depict cruelty, dog-fighting,
              untitled &ldquo;guard dog / personal protection&rdquo;
              work for sale, an animal in distress, or any activity
              illegal in the jurisdiction in which the footage was
              recorded;
            </li>
            <li>
              any identifiable person in frame &mdash; including any
              minor decoy, helper, judge, or family member &mdash; has
              consented (or a guardian has consented on behalf of a
              minor) to the use of their likeness on this public
              listing;
            </li>
            <li>
              you have all rights to the underlying music, voice-over,
              and footage, and the upload does not infringe any
              copyright, trademark, right of publicity, or other right.
            </li>
          </ul>
          <strong>Platform shield.</strong> The display of a working /
          sport video on a dog profile is <strong>not</strong> a
          representation by Armstrong K9 Registry as to the depicted
          dog&rsquo;s training, certifications, titles, temperament,
          health, or suitability for any purpose. We do not lab-test,
          score, certify, or otherwise verify the conduct depicted. We
          do not assess whether the dog is safe with children, other
          animals, or in any home or working environment. The seller is
          solely responsible for the accuracy of any claim implied by
          the footage and for the use a buyer makes of the depicted
          animal. Inquiries, deposits, contracts, and any post-sale
          conduct remain governed by &sect;4 (marketplace neutrality)
          and &sect;5e/5f (provider liability shield). The Registry may
          remove a working video on a good-faith report of any
          Acceptable-Use violation, may require an attestation at
          upload time, and may suspend a seller account for repeat
          violations. DMCA takedowns are handled per &sect;8.
        </li>
        <li>
          <strong>Kennel directory placement.</strong> The Registry curates
          the <code>/kennels</code> directory and selects a Featured kennel
          slot at its sole discretion. Featured placement is not guaranteed
          by any subscription tier and may rotate, be suspended, or be
          removed at any time. Sort order, search ranking, and the Featured
          rotation are not commitments included in any paid plan.
        </li>
        <li>
          <strong>DNA-Verified badge.</strong> The DNA-Verified badge means
          an Armstrong K9 Registry administrator reviewed a third-party DNA
          panel report you submitted and found the parentage claim
          consistent with that report. We do not lab-test your dog. We are
          not a genetics laboratory and we make no representation as to the
          accuracy of the underlying lab&apos;s methodology or results.
          Refunding the verification fee revokes the badge.
        </li>
      </ul>

      <h2>8. DMCA / copyright takedown</h2>
      <p>
        To file a DMCA notice, email{" "}
        <a href="mailto:dmca@armstrongk9registry.com">dmca@armstrongk9registry.com</a>{" "}
        with the work allegedly infringed, the URL of the infringing listing,
        your physical address, a good-faith statement, and a signature.
        Counter-notices may be sent to the same address. Repeat infringers
        will be terminated.
      </p>

      <h2>9. Termination</h2>
      <ul>
        <li>You may deactivate your account at any time from Settings.</li>
        <li>
          We may suspend or terminate your account for breach of these terms,
          fraud, or by operation of law.
        </li>
        <li>
          On termination: paid-but-unused balances are refunded per the{" "}
          <Link to="/refund">Refund Policy</Link>. Public records you
          contributed (pedigree links) may remain visible because they are
          part of the shared registry.
        </li>
      </ul>

      <h2>10. Disclaimer of warranties</h2>
      <p>
        The Registry is provided &ldquo;as is&rdquo; without warranties of
        merchantability, fitness for a particular purpose, or non-infringement.
        We do not guarantee uninterrupted service or error-free operation. We
        are not the breeder of any listed dog and make no warranties about the
        health, temperament, or pedigree accuracy of any dog listed by a third
        party.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our total liability for any
        claim related to the Registry is limited to the greater of $100 or the
        fees you paid us in the 12 months preceding the claim. We are not
        liable for indirect, incidental, consequential, or punitive damages.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless the Registry from any
        claim arising out of (a) your content, (b) your sale or purchase of a
        dog through our marketplace, (c) your breach of these terms, or
        (d) your violation of any law or third-party right.
      </p>

      <h2>13. Governing law &amp; arbitration</h2>
      <p>
        These terms are governed by the laws of the State of California, United
        States. Disputes that are not resolved through good-faith negotiation
        will be settled by binding individual arbitration administered by the
        American Arbitration Association under its Consumer Arbitration Rules,
        with the seat of arbitration in Shasta County, California, United
        States. The arbitrator will decide all issues.
      </p>
      <p>
        <strong>Class action waiver.</strong> You and we waive the right to
        bring claims as a plaintiff or class member in any class or
        representative action. If this waiver is held unenforceable for a
        particular claim, that claim must be brought in court (the rest of
        this section still applies).
      </p>
      <p>
        <strong>30-day opt-out.</strong> You may opt out of arbitration by
        emailing{" "}
        <a href="mailto:legal@armstrongk9registry.com">legal@armstrongk9registry.com</a>{" "}
        within 30 days of first agreeing to these terms with the subject line
        &ldquo;Arbitration opt-out&rdquo;.
      </p>

      <h2>14. Changes</h2>
      <p>
        We will give at least 30 days&rsquo; notice (in-app banner + email)
        before material changes take effect. Continued use of the Registry
        after the effective date means you accept the updated terms.
      </p>

      <h2>15. Contact</h2>
      <p>
        Legal notices:{" "}
        <a href="mailto:legal@armstrongk9registry.com">legal@armstrongk9registry.com</a>
        <br />
        General support:{" "}
        <a href="mailto:support@armstrongk9registry.com">support@armstrongk9registry.com</a>
      </p>
    </LegalLayout>
  );
}
